import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "results", "loading", "generateBtn", "ideasList", "manualForm", "manualTitle", "manualDescription", "manualPlatform", "manualContentType", "manualSaveBtn", "emptyState"]
  static values = {
    generateUrl: String,
    createUrl: String,
    clientId: Number
  }

  connect() {
    this.panelOpen = false
  }

  togglePanel() {
    this.panelOpen = !this.panelOpen
    if (this.hasPanelTarget) {
      this.panelTarget.classList.toggle("hidden", !this.panelOpen)
    }
  }

  closePanel() {
    this.panelOpen = false
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add("hidden")
    }
  }

  toggleManualForm() {
    if (this.hasManualFormTarget) {
      this.manualFormTarget.classList.toggle("hidden")
    }
  }

  async saveManualIdea() {
    const title = this.hasManualTitleTarget ? this.manualTitleTarget.value.trim() : ""
    if (!title) {
      this.manualTitleTarget.focus()
      this.manualTitleTarget.classList.add("border-[#E57373]")
      setTimeout(() => this.manualTitleTarget.classList.remove("border-[#E57373]"), 2000)
      return
    }

    const btn = this.hasManualSaveBtnTarget ? this.manualSaveBtnTarget : null
    if (btn) {
      btn.disabled = true
      btn.textContent = "Saving..."
    }

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          content_idea: {
            title: title,
            description: this.hasManualDescriptionTarget ? this.manualDescriptionTarget.value.trim() : "",
            platform: this.hasManualPlatformTarget ? this.manualPlatformTarget.value : "TikTok",
            content_type: this.hasManualContentTypeTarget ? this.manualContentTypeTarget.value : "Short-form Video",
            source: "manual"
          }
        })
      })

      const data = await response.json()
      if (data.success || response.ok) {
        // Clear form
        if (this.hasManualTitleTarget) this.manualTitleTarget.value = ""
        if (this.hasManualDescriptionTarget) this.manualDescriptionTarget.value = ""
        if (this.hasManualPlatformTarget) this.manualPlatformTarget.selectedIndex = 0
        if (this.hasManualContentTypeTarget) this.manualContentTypeTarget.selectedIndex = 0
        // Hide form
        this.manualFormTarget.classList.add("hidden")
        // Reload to show new idea in list
        window.location.reload()
      } else {
        alert(data.error || "Failed to save idea. Please try again.")
      }
    } catch (error) {
      alert("Network error. Please try again.")
    } finally {
      if (btn) {
        btn.disabled = false
        btn.textContent = "Save Idea"
      }
    }
  }

  async generate(event) {
    event.preventDefault()

    const form = event.target.closest("form") || event.target.closest("[data-content-ideas-target='panel']")
    const focus = form.querySelector("[name='focus']")?.value || "general"
    const count = form.querySelector("[name='count']")?.value || "5"
    const includeTrends = form.querySelector("[name='include_trends']")?.checked !== false

    // Show loading state
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
    if (this.hasResultsTarget) this.resultsTarget.classList.add("hidden")
    if (this.hasGenerateBtnTarget) {
      this.generateBtnTarget.disabled = true
      this.generateBtnTarget.textContent = "Generating..."
    }

    try {
      const response = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        },
        body: JSON.stringify({ focus, count, include_trends: includeTrends.toString() })
      })

      const data = await response.json()

      if (response.ok && data.ideas) {
        this.renderIdeas(data.ideas)
      } else {
        this.showError(data.error || "Failed to generate ideas.")
      }
    } catch (error) {
      this.showError("Network error. Please try again.")
    } finally {
      if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
      if (this.hasGenerateBtnTarget) {
        this.generateBtnTarget.disabled = false
        this.generateBtnTarget.textContent = "Generate Ideas"
      }
    }
  }

  renderIdeas(ideas) {
    if (!this.hasResultsTarget) return

    const html = ideas.map((idea, index) => this.ideaCardHtml(idea, index)).join("")
    this.resultsTarget.innerHTML = `
      <div class="flex items-center justify-between mb-4">
        <h4 class="text-sm font-semibold text-[#2D2D2D]">Generated ${ideas.length} Ideas</h4>
        <button data-action="click->content-ideas#clearResults" class="text-xs text-[#8C8C8C] hover:text-[#2D2D2D]">Clear</button>
      </div>
      <div class="space-y-4">${html}</div>
    `
    this.resultsTarget.classList.remove("hidden")
  }

  ideaCardHtml(idea, index) {
    const platformBadge = this.platformBadgeHtml(idea.platform)
    const hashtags = idea.hashtags || ""
    const trendingSound = idea.trending_sound && idea.trending_sound !== "" ?
      `<div class="flex items-center space-x-1 mt-1">
        <svg class="w-3 h-3 text-[#C4A484]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/>
        </svg>
        <span class="text-xs text-[#C4A484]">${this.escapeHtml(idea.trending_sound)}</span>
      </div>` : ""

    return `
      <div class="p-4 border border-[#E8E4DF] rounded-lg bg-white" data-idea-index="${index}">
        <div class="flex items-start justify-between mb-2">
          <div class="flex-1 min-w-0">
            <div class="flex items-center space-x-2 mb-1">
              <h5 class="text-sm font-semibold text-[#2D2D2D]">${this.escapeHtml(idea.title)}</h5>
            </div>
            <div class="flex items-center space-x-2">
              ${platformBadge}
              <span class="px-2 py-0.5 text-[10px] rounded-full bg-[#F5F3F0] text-[#8C8C8C]">${this.escapeHtml(idea.content_type)}</span>
              ${idea.suggested_time ? `<span class="text-[10px] text-[#8C8C8C]">${this.escapeHtml(idea.suggested_time)}</span>` : ""}
            </div>
          </div>
        </div>
        <p class="text-xs text-[#8C8C8C] mt-2">${this.escapeHtml(idea.description)}</p>
        <div class="mt-3 p-3 bg-[#F5F3F0] rounded-lg">
          <p class="text-xs text-[#2D2D2D] italic">"${this.escapeHtml(idea.caption)}"</p>
        </div>
        ${hashtags ? `<p class="text-[10px] text-[#C4A484] mt-2">${this.escapeHtml(hashtags)}</p>` : ""}
        ${trendingSound}
        <div class="flex items-center space-x-2 mt-3 pt-3 border-t border-[#E8E4DF]">
          <button data-action="click->content-ideas#createDraft"
                  data-idea='${this.escapeAttr(JSON.stringify(idea))}'
                  class="px-3 py-1.5 text-xs font-medium text-white bg-[#C4A484] hover:bg-[#B39474] rounded-lg transition-colors cursor-pointer">
            Create as Draft
          </button>
          <button data-action="click->content-ideas#saveForLater"
                  data-idea='${this.escapeAttr(JSON.stringify(idea))}'
                  class="px-3 py-1.5 text-xs font-medium text-[#2D2D2D] bg-[#F5F3F0] hover:bg-[#E8E4DF] rounded-lg transition-colors cursor-pointer">
            Save for Later
          </button>
          <button data-action="click->content-ideas#skipIdea"
                  class="px-3 py-1.5 text-xs font-medium text-[#8C8C8C] hover:text-[#2D2D2D] transition-colors cursor-pointer">
            Skip
          </button>
        </div>
      </div>
    `
  }

  platformBadgeHtml(platform) {
    const p = (platform || "").toLowerCase()
    if (p.includes("tiktok")) {
      return '<span class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-[#2D2D2D] text-white">TikTok</span>'
    } else if (p.includes("story")) {
      return '<span class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">IG Story</span>'
    } else if (p.includes("carousel")) {
      return '<span class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">IG Carousel</span>'
    } else if (p.includes("reel")) {
      return '<span class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">IG Reel</span>'
    } else if (p.includes("youtube")) {
      return '<span class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-red-600 text-white">YouTube</span>'
    } else {
      return '<span class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">Instagram</span>'
    }
  }

  async createDraft(event) {
    const btn = event.currentTarget
    const idea = JSON.parse(btn.dataset.idea)
    btn.disabled = true
    btn.textContent = "Creating..."

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          content_idea: {
            title: idea.title,
            platform: idea.platform,
            content_type: idea.content_type,
            description: idea.description,
            caption: idea.caption,
            hashtags: idea.hashtags,
            trending_sound: idea.trending_sound,
            suggested_time: idea.suggested_time
          },
          use_as_draft: true
        })
      })

      const data = await response.json()
      if (data.redirect_url) {
        window.location.href = data.redirect_url
      } else if (data.success) {
        btn.textContent = "Draft Created"
        btn.classList.remove("bg-[#C4A484]", "hover:bg-[#B39474]")
        btn.classList.add("bg-[#9CAF88]", "text-white")
      }
    } catch (error) {
      btn.disabled = false
      btn.textContent = "Create as Draft"
      alert("Failed to create draft. Please try again.")
    }
  }

  async saveForLater(event) {
    const btn = event.currentTarget
    const idea = JSON.parse(btn.dataset.idea)
    btn.disabled = true
    btn.textContent = "Saving..."

    // Build save URL from the create URL (same endpoint, different param)
    const saveUrl = this.createUrlValue

    try {
      const response = await fetch(saveUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          content_idea: {
            title: idea.title,
            platform: idea.platform,
            content_type: idea.content_type,
            description: idea.description,
            caption: idea.caption,
            hashtags: idea.hashtags,
            trending_sound: idea.trending_sound,
            suggested_time: idea.suggested_time
          }
        })
      })

      const data = await response.json()
      if (data.success) {
        btn.textContent = "Saved"
        btn.classList.remove("bg-[#F5F3F0]", "hover:bg-[#E8E4DF]")
        btn.classList.add("bg-[#9CAF88]/20", "text-[#9CAF88]")
      }
    } catch (error) {
      btn.disabled = false
      btn.textContent = "Save for Later"
    }
  }

  skipIdea(event) {
    const card = event.currentTarget.closest("[data-idea-index]")
    if (card) {
      card.style.transition = "opacity 0.2s ease, max-height 0.3s ease"
      card.style.opacity = "0"
      card.style.maxHeight = card.scrollHeight + "px"
      setTimeout(() => {
        card.style.maxHeight = "0"
        card.style.overflow = "hidden"
        card.style.padding = "0"
        card.style.margin = "0"
        card.style.border = "none"
      }, 200)
      setTimeout(() => card.remove(), 500)
    }
  }

  clearResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
    }
  }

  showError(message) {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = `
        <div class="p-4 bg-[#E57373]/10 border border-[#E57373]/20 rounded-lg">
          <p class="text-sm text-[#E57373]">${this.escapeHtml(message)}</p>
        </div>
      `
      this.resultsTarget.classList.remove("hidden")
    }
  }

  // Ideas tab actions (for saved ideas)
  async useIdea(event) {
    const btn = event.currentTarget
    const ideaId = btn.dataset.ideaId
    btn.disabled = true
    btn.textContent = "Creating..."

    try {
      const url = this.createUrlValue.replace(/\/content_ideas$/, `/content_ideas/${ideaId}/use_idea`)
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        }
      })

      const data = await response.json()
      if (data.redirect_url) {
        window.location.href = data.redirect_url
      }
    } catch (error) {
      btn.disabled = false
      btn.textContent = "Use This"
    }
  }

  async dismissIdea(event) {
    const btn = event.currentTarget
    const ideaId = btn.dataset.ideaId
    const card = btn.closest("[data-idea-id]")

    try {
      const url = this.createUrlValue.replace(/\/content_ideas$/, `/content_ideas/${ideaId}/dismiss`)
      await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        }
      })

      if (card) {
        card.style.transition = "opacity 0.2s ease"
        card.style.opacity = "0"
        setTimeout(() => card.remove(), 200)
      }
    } catch (error) {
      // Silently fail
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }

  escapeAttr(text) {
    return (text || "").replace(/'/g, "&#39;").replace(/"/g, "&quot;")
  }
}
